import { AvatarGroup, Button, IconButton, IconTile, PageHeader, SpaceIcon, Tooltip } from "@design";
import { Lock, Plus, Settings } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import type { SpaceHubResponse } from "@/api";
import { hueOf, spaceIcon } from "@/features/shell/spaceIcons";
import { formatList } from "@/i18n/format";
import { newSpaceChatPath } from "./spaceChat";

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");

export function SpaceHeader({
  space,
  onOpenMembers,
  onOpenSettings,
}: Readonly<{ space: SpaceHubResponse; onOpenMembers: () => void; onOpenSettings: () => void }>) {
  const { t } = useTranslation("spaces", { keyPrefix: "detail" });
  const navigate = useNavigate();
  const teams = space.teamNames ?? [];
  let shared = t("sharedWithMembers");
  if (space.isPersonal) shared = t("privateToYou");
  else if (teams.length > 0) shared = t("sharedWith", { teams: formatList(teams) });

  return (
    <PageHeader
      icon={
        space.isPersonal ? (
          <IconTile size="lg" variant="brand">
            <Lock size={20} />
          </IconTile>
        ) : (
          <SpaceIcon icon={spaceIcon(space.icon, 20)} hue={hueOf(space.color)} size="lg" />
        )
      }
      title={space.name}
      subtitle={`${t("chats", { count: space.stats.myChatCount ?? 0 })} · ${shared}`}
      actions={
        <span className="flex shrink-0 items-center gap-2">
          {!space.isPersonal && (space.members ?? []).length > 0 && (
            <AvatarGroup
              avatars={(space.members ?? []).map((m) => ({ key: m.userUuid, initials: initials(m.name), alt: m.name }))}
              size="sm"
              max={4}
              onClick={onOpenMembers}
              aria-label={t("members")}
            />
          )}
          <Button
            variant="secondary"
            size="sm"
            leadingIcon={<Plus size={14} />}
            onClick={() => navigate(newSpaceChatPath(space.uuid))}
          >
            {t("newChat")}
          </Button>
          {space.canEdit && (
            <Tooltip content={t("settings")}>
              <IconButton
                icon={<Settings />}
                aria-label={t("settings")}
                variant="ghost"
                size="md"
                flat
                onClick={onOpenSettings}
              />
            </Tooltip>
          )}
        </span>
      }
    />
  );
}
